import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { finishSocial, type SocialResult } from '../../api/oauth';
import { useSession } from '../../state/useSession';
import { trackPixel } from '../../analytics/metaPixel';
import { funnelStep } from '../../analytics/funnel';
import { returnTrip } from '../../shell/returnTo';
import { leaveFor } from '../../platform/leave';
import { useDraft } from '../../state/draft';
import { noteWaiting } from '../../api/build';
import { Button, Screen } from '../../ui';

/** What each failure says, in words a person can act on. */
const SAID: Record<string, string> = {
  cancelled: 'Sign-in was cancelled. You can try again, or continue with your email.',
  provider_error: 'That sign-in didn’t go through. You can try again, or continue with your email.',
  state_mismatch: 'That sign-in link had expired. Please start again.',
  token_exchange: 'We couldn’t finish signing you in. Please try again.',
};

/**
 * Where Google and Apple send people back (via Cognito): /auth/callback?code=…&state=…
 *
 * Finishes the sign-in (api/oauth.ts), says it to the journey log the way an emailed code does —
 * a new account is a registration (`CompleteRegistration`, `signup_completed`), an old one a
 * sign-in — and goes on to wherever the person was going, with their plan brought back from the
 * tab's storage by the screen they land on (Plan.tsx restores a draft stashed before the trip).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { adopt } = useSession();
  const [failed, setFailed] = useState<{ reason: string; from?: string } | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    void finishSocial(window.location.search).then((result: SocialResult) => {
      if (!result.ok) {
        if (result.retryAt) {
          // The account was just joined to the one this email had: once more, straight through.
          leaveFor(result.retryAt);
          return;
        }
        if (result.provider) funnelStep('signup_failed', { method: result.provider, code: result.reason });
        setFailed({ reason: result.reason, from: result.from });
        return;
      }
      adopt(result.user);
      if (result.created) {
        trackPixel('CompleteRegistration');
        funnelStep('signup_completed', { method: result.provider });
        if (result.from?.startsWith('/new/plan')) {
          const d = useDraft.getState();
          void noteWaiting(d.plan?.appName || d.appName || '', d.description);
        }
      } else {
        funnelStep('signin_completed', { method: result.provider });
      }
      const trip = returnTrip(result.from);
      navigate(trip.to, { replace: true, ...(trip.state ? { state: trip.state } : {}) });
    });
  }, [adopt, navigate]);

  if (!failed) {
    return (
      <Screen>
        <p role="status" className="type-body">Signing you in…</p>
      </Screen>
    );
  }
  return (
    <Screen title="Sign-in didn’t finish">
      <p role="alert" data-error-code={`oauth_${failed.reason}`} className="type-body">{SAID[failed.reason] ?? SAID.provider_error}</p>
      <Button title="Back to sign up" onPress={() => navigate('/sign-up', { replace: true, state: failed.from ? { from: failed.from } : undefined })} />
    </Screen>
  );
}
